import React, { useState } from 'react';
import StreamIntroPage from './StreamIntroPage';
import { Box } from 'grommet';
import {
  LiveStreamSetUpSteps,
  LiveStreamSetUpStepsTracking,
} from '@theme/theme.d';
import StreamingSelectionPage from './StreamingSelectionPage';
import NextGamePage from './NextGamePage';
import HowWillYouScorePage from './HowWillYouScorePage';
import InviteTeamManagersPage from './InviteTeamManagersPage';
import WhosPlayingPage from './WhosPlayingPage';
import useTeamTrackInfo from '@hooks/useTeamTrackInfo';
import { RouteComponentProps } from 'react-router-dom';
import { analytics } from '@services/analytics';

interface LiveStreamIntroPageProps extends RouteComponentProps {
  teamId: string;
  onClose: () => void;
  isAdmin: boolean;
  teamName: string;
  sportType: string;
  step?: LiveStreamSetUpSteps;
}

const LiveStreamIntroPage: React.FC<LiveStreamIntroPageProps> = ({
  teamId,
  onClose,
  isAdmin,
  teamName,
  sportType,
  step = LiveStreamSetUpSteps.INTRO_PAGE,
  history,
}) => {
  // Constants
  const modalHeight = {
    height: '100%',
    min: '650px',
  };

  // State
  const [currentStep, setCurrentStep] = useState<LiveStreamSetUpSteps>(step);

  // Hooks
  const { teamInfo } = useTeamTrackInfo(teamId);

  // Handlers
  const handleStepChange = (
    newStep: LiveStreamSetUpSteps,
    completedStep?: LiveStreamSetUpStepsTracking,
    eventProps?: object // Add optional eventProps here
  ) => {
    if (completedStep) {
      handleTrackingEvent('nextStep', completedStep, undefined, eventProps); // Pass eventProps to tracking
    }
    setCurrentStep(newStep);
  };

  const handleTrackingEvent = (
    type: 'nextStep' | 'goback',
    nextStep: LiveStreamSetUpStepsTracking,
    actualStep?: LiveStreamSetUpStepsTracking,
    eventProps?: object // Add optional eventProps here
  ) => {
    const baseProps = {
      teamId: teamId,
      sport: sportType,
      ...(eventProps || {}), // Merge additional eventProps dynamically
    };

    switch (type) {
      case 'nextStep':
        analytics.track('Livestream Setup Step Completed', {
          ...baseProps, // Include base properties and eventProps
          stepCompleted: nextStep,
        });
        break;
      case 'goback':
        analytics.track('Livestream Setup Step Back Clicked', {
          ...baseProps, // Include base properties and eventProps
          currentStep: actualStep,
          previousStep: nextStep,
        });
        break;
    }
  };

  // Renderers
  const renderScreenByStep = () => {
    switch (currentStep) {
      case LiveStreamSetUpSteps.INTRO_PAGE:
        return (
          <Box height={modalHeight}>
            <StreamIntroPage
              modalHeight={modalHeight}
              onClose={onClose}
              onContinue={() => {
                handleStepChange(
                  LiveStreamSetUpSteps.NEXT_GAME_SELECTION,
                  LiveStreamSetUpStepsTracking.INTRO_PAGE
                );
                analytics.track('Livestream Setup Entered', {
                  teamId: teamId,
                  source: 'get started',
                  sport: sportType,
                });
              }}
            />
          </Box>
        );

      case LiveStreamSetUpSteps.NEXT_GAME_SELECTION:
        return (
          <Box height={modalHeight}>
            <NextGamePage
              modalHeight={modalHeight}
              sportType={sportType}
              teamId={teamId}
              teamName={teamName}
              onContinue={(trackingData) => {
                handleStepChange(
                  LiveStreamSetUpSteps.SCORE_GAME_SELECTION,
                  LiveStreamSetUpStepsTracking.NEXT_GAME_SELECTION,
                  trackingData
                );
                history.replace(`${window.location.pathname}?tab=`);
              }}
            />
          </Box>
        );

      case LiveStreamSetUpSteps.SCORE_GAME_SELECTION:
        return (
          <Box height={modalHeight}>
            <HowWillYouScorePage
              modalHeight={modalHeight}
              onContinue={(inviteManagers) => {
                const scoringOption = inviteManagers
                  ? 'Someone else will help'
                  : 'Using another device';

                if (inviteManagers) {
                  handleStepChange(
                    LiveStreamSetUpSteps.INVITE_TEAM_MANAGERS,
                    LiveStreamSetUpStepsTracking.SCORE_GAME_SELECTION,
                    { scoringOption }
                  );
                } else {
                  if (teamInfo?.playerCount === 0) {
                    handleStepChange(
                      LiveStreamSetUpSteps.ROSTER_SELECTION,
                      LiveStreamSetUpStepsTracking.SCORE_GAME_SELECTION,
                      { scoringOption }
                    );
                  } else {
                    handleStepChange(
                      LiveStreamSetUpSteps.STREAMING_SELECTION,
                      LiveStreamSetUpStepsTracking.SCORE_GAME_SELECTION,
                      { scoringOption }
                    );
                  }
                }
              }}
            />
          </Box>
        );
      case LiveStreamSetUpSteps.INVITE_TEAM_MANAGERS:
        return (
          <Box height={modalHeight} style={{ zIndex: 99999 }}>
            <InviteTeamManagersPage
              modalHeight={modalHeight}
              teamId={teamId}
              sportType={sportType}
              onBack={() => {
                handleStepChange(LiveStreamSetUpSteps.SCORE_GAME_SELECTION),
                  handleTrackingEvent(
                    'goback',
                    LiveStreamSetUpStepsTracking.INVITE_TEAM_MANAGERS,
                    LiveStreamSetUpStepsTracking.SCORE_GAME_SELECTION
                  );
              }}
              onClose={onClose}
              onContinue={() => {
                if (teamInfo?.playerCount === 0) {
                  handleStepChange(
                    LiveStreamSetUpSteps.ROSTER_SELECTION,
                    LiveStreamSetUpStepsTracking.INVITE_TEAM_MANAGERS
                  );
                } else {
                  handleStepChange(
                    LiveStreamSetUpSteps.STREAMING_SELECTION,
                    LiveStreamSetUpStepsTracking.INVITE_TEAM_MANAGERS
                  );
                }
              }}
            />
          </Box>
        );

      case LiveStreamSetUpSteps.ROSTER_SELECTION:
        return (
          <Box height={modalHeight}>
            <WhosPlayingPage
              modalHeight={modalHeight}
              teamId={teamId}
              sportType={sportType}
              onClose={onClose}
              onContinue={(playerCount) => {
                handleStepChange(
                  LiveStreamSetUpSteps.STREAMING_SELECTION,
                  LiveStreamSetUpStepsTracking.ROSTER_SELECTION,
                  { playerCount }
                );
                history.replace(`${window.location.pathname}?tab=`);
              }}
            />
          </Box>
        );

      case LiveStreamSetUpSteps.STREAMING_SELECTION:
        return (
          <Box height={modalHeight}>
            <StreamingSelectionPage
              modalHeight={modalHeight}
              onContinue={onClose}
              teamId={teamId}
              isAdmin={isAdmin}
              teamName={teamName}
              sportType={sportType}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return <>{renderScreenByStep()}</>;
};

export default LiveStreamIntroPage;
